import { SupportedLanguage, SUPPORTED_LANGUAGES, TIER1_LANGUAGES, isRTL } from './routes';
import enTranslations from '@/locales/en.json';
import frTranslations from '@/locales/fr.json';
import esTranslations from '@/locales/es.json';
import arTranslations from '@/locales/ar.json';
import deTranslations from '@/locales/de.json';
import ruTranslations from '@/locales/ru.json';
import zhTranslations from '@/locales/zh.json';
import itTranslations from '@/locales/it.json';
import ptTranslations from '@/locales/pt.json';
import plTranslations from '@/locales/pl.json';
import jaTranslations from '@/locales/ja.json';
import nlTranslations from '@/locales/nl.json';
import trTranslations from '@/locales/tr.json';
import koTranslations from '@/locales/ko.json';
import svTranslations from '@/locales/sv.json';
import daTranslations from '@/locales/da.json';
import hiTranslations from '@/locales/hi.json';
import roTranslations from '@/locales/ro.json';
import csTranslations from '@/locales/cs.json';
import huTranslations from '@/locales/hu.json';
import fiTranslations from '@/locales/fi.json';
import thTranslations from '@/locales/th.json';
import viTranslations from '@/locales/vi.json';
import elTranslations from '@/locales/el.json';
import noTranslations from '@/locales/no.json';
import idTranslations from '@/locales/id.json';
import msTranslations from '@/locales/ms.json';
import tlTranslations from '@/locales/tl.json';
import bnTranslations from '@/locales/bn.json';

export type { SupportedLanguage };
export { SUPPORTED_LANGUAGES, TIER1_LANGUAGES, isRTL };

// Language metadata - 29 supported languages (Global BPO Strategy)
export const languages: { code: SupportedLanguage; name: string; flag: string; dir: 'ltr' | 'rtl' }[] = [
  { code: 'en', name: 'English', flag: '🇬🇧', dir: 'ltr' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'es', name: 'Español', flag: '🇪🇸', dir: 'ltr' },
  { code: 'ar', name: 'العربية', flag: '🇦🇪', dir: 'rtl' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹', dir: 'ltr' },
  { code: 'pt', name: 'Português', flag: '🇧🇷', dir: 'ltr' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱', dir: 'ltr' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱', dir: 'ltr' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺', dir: 'ltr' },
  { code: 'zh', name: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'ja', name: '日本語', flag: '🇯🇵', dir: 'ltr' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷', dir: 'ltr' },
  { code: 'ko', name: '한국어', flag: '🇰🇷', dir: 'ltr' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪', dir: 'ltr' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰', dir: 'ltr' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳', dir: 'ltr' },
  { code: 'ro', name: 'Română', flag: '🇷🇴', dir: 'ltr' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿', dir: 'ltr' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺', dir: 'ltr' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮', dir: 'ltr' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭', dir: 'ltr' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳', dir: 'ltr' },
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷', dir: 'ltr' },
  { code: 'no', name: 'Norsk', flag: '🇳🇴', dir: 'ltr' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩', dir: 'ltr' },
  { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾', dir: 'ltr' },
  { code: 'tl', name: 'Filipino', flag: '🇵🇭', dir: 'ltr' },
  { code: 'bn', name: 'বাংলা', flag: '🇧🇩', dir: 'ltr' },
];

// Base translation type from English (the complete one)
export type TranslationType = typeof enTranslations;

// Translations map - EN is complete, others may have missing keys (will fallback)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translationsMap: Record<string, any> = {
  en: enTranslations,
  fr: frTranslations,
  es: esTranslations,
  ar: arTranslations,
  de: deTranslations,
  it: itTranslations,
  pt: ptTranslations,
  nl: nlTranslations,
  pl: plTranslations,
  ru: ruTranslations,
  zh: zhTranslations,
  ja: jaTranslations,
  tr: trTranslations,
  ko: koTranslations,
  sv: svTranslations,
  da: daTranslations,
  hi: hiTranslations,
  ro: roTranslations,
  cs: csTranslations,
  hu: huTranslations,
  fi: fiTranslations,
  th: thTranslations,
  vi: viTranslations,
  el: elTranslations,
  no: noTranslations,
  id: idTranslations,
  ms: msTranslations,
  tl: tlTranslations,
  bn: bnTranslations,
};

// Deep merge function to combine partial translations with English fallback
function deepMerge<T extends object>(target: T, source: Partial<T>): T {
  const result = { ...target };
  
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = target[key];
      
      if (
        sourceValue !== undefined &&
        typeof sourceValue === 'object' &&
        sourceValue !== null &&
        !Array.isArray(sourceValue) &&
        typeof targetValue === 'object' &&
        targetValue !== null &&
        !Array.isArray(targetValue)
      ) {
        (result as Record<string, unknown>)[key] = deepMerge(
          targetValue as object,
          sourceValue as Partial<typeof targetValue>
        );
      } else if (sourceValue !== undefined) {
        (result as Record<string, unknown>)[key] = sourceValue;
      }
    }
  }
  
  return result;
}

// Get translation with fallback to English for missing keys
export function getTranslation(lang: SupportedLanguage): TranslationType {
  const langTranslations = translationsMap[lang];
  
  if (!langTranslations || lang === 'en') {
    return enTranslations;
  }
  
  // Merge with English as fallback
  return deepMerge(enTranslations, langTranslations as Partial<TranslationType>);
}

// Safe key access with fallback
export function t(translations: TranslationType, path: string): string {
  const keys = path.split('.');
  let value: unknown = translations;
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = (value as Record<string, unknown>)[key];
    } else {
      // Fallback to English
      value = enTranslations;
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = (value as Record<string, unknown>)[k];
        } else {
          return path; // Return key if not found
        }
      }
      break;
    }
  }
  
  return typeof value === 'string' ? value : path;
}

export function getDirection(lang: SupportedLanguage): 'ltr' | 'rtl' {
  return isRTL(lang) ? 'rtl' : 'ltr';
}

const STORAGE_KEY = 'callitdev-language';

export function getStoredLanguage(): SupportedLanguage {
  if (typeof window === 'undefined') return 'en';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
    return stored as SupportedLanguage;
  }
  return 'en';
}

export function setStoredLanguage(lang: SupportedLanguage): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, lang);
  }
}
